import { useEffect, useState } from 'react';
import { Gear, Image as ImageIcon } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { useTheme } from '../../contexts/ThemeContext';
import SingleImageUploader from '../../components/SingleImageUploader';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSettingsPage() {
    const { theme } = useTheme();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [config, setConfig] = useState({
        hero_title: '',
        hero_subtitle: '',
        mission_text: '',
        vision_text: '',
        coordonnees_telephone: '',
        coordonnees_email: '',
        coordonnees_adresse: '',
        logo_global: '',
        logo_luxury: '',
        global_title: '',
        global_subtitle: '',
        global_description: '',
        luxury_title: '',
        luxury_subtitle: '',
        luxury_description: ''
    });

    useEffect(() => {
        fetchConfig();
    }, []);

    const fetchConfig = async () => {
        try {
            const response = await axios.get(`${API}/config`);
            setConfig(response.data);
        } catch (error) {
            console.error('Error fetching config:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        setConfig(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await axios.put(`${API}/config`, config);
            toast.success('Configuration enregistrée');
        } catch (error) {
            console.error('Error saving config:', error);
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    const cardClass = theme === 'dark' 
        ? 'bg-mmd-surface-dark border border-white/10' 
        : 'bg-white border border-black/10';
    const titleClass = theme === 'dark' ? 'text-white' : 'text-mmd-navy';
    const labelClass = theme === 'dark' ? 'text-white' : 'text-mmd-navy';
    const inputClass = theme === 'dark' 
        ? 'mt-1 bg-mmd-dark-bg border-white/20 text-white' 
        : 'mt-1 bg-white border-black/20 text-mmd-navy';

    if (loading) {
        return (
            <div className="space-y-4">
                {[1, 2, 3, 4].map(i => (
                    <div key={i} className={`rounded-lg p-4 animate-pulse h-16 ${theme === 'dark' ? 'bg-mmd-surface-dark' : 'bg-white'}`} />
                ))}
            </div>
        );
    }

    return (
        <div data-testid="admin-settings-page">
            <div className="mb-6">
                <h1 className={`font-heading text-2xl font-medium ${titleClass}`}>Paramètres</h1>
                <p className={`text-sm ${theme === 'dark' ? 'text-mmd-silver/70' : 'text-mmd-navy/60'}`}>Configurez le contenu de votre site</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
                {/* Logos Section */}
                <div className={`rounded-xl p-6 ${cardClass}`}>
                    <div className="flex items-center gap-2 mb-4">
                        <ImageIcon size={20} className={theme === 'dark' ? 'text-mmd-gold' : 'text-mmd-navy'} />
                        <h2 className={`font-heading text-lg font-medium ${titleClass}`}>Logos du site</h2>
                    </div>
                    <p className={`text-sm mb-6 ${theme === 'dark' ? 'text-mmd-silver/70' : 'text-mmd-navy/60'}`}>
                        Uploadez les logos qui apparaissent dans le header et le footer du site.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                            <Label className={`${labelClass} mb-2 block`}>Logo MMD Global (gauche)</Label>
                            <SingleImageUploader
                                image={config.logo_global}
                                onImageChange={(url) => setConfig(prev => ({ ...prev, logo_global: url }))}
                                label="Logo MMD Global"
                                testId="logo-global"
                            />
                        </div>
                        <div>
                            <Label className={`${labelClass} mb-2 block`}>Logo MMD Luxury Cars (droite)</Label>
                            <SingleImageUploader
                                image={config.logo_luxury}
                                onImageChange={(url) => setConfig(prev => ({ ...prev, logo_luxury: url }))}
                                label="Logo MMD Luxury"
                                testId="logo-luxury"
                            />
                        </div>
                    </div>
                </div>

                {/* Monde MMD Global */}
                <div className={`rounded-xl p-6 ${cardClass}`}>
                    <div className="flex items-center gap-3 mb-4">
                        {config.logo_global && (
                            <img src={config.logo_global} alt="MMD Global" className="w-10 h-10 rounded-full object-cover" />
                        )}
                        <h2 className={`font-heading text-lg font-medium ${titleClass}`}>Monde MMD Global (Matériel de bureau)</h2>
                    </div>
                    <p className={`text-sm mb-4 ${theme === 'dark' ? 'text-mmd-silver/70' : 'text-mmd-navy/60'}`}>
                        Titre et texte affichés dans la section "Nos Activités" de la page d'accueil.
                    </p>
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="global_subtitle" className={labelClass}>Sous-titre (Marque)</Label>
                                <Input
                                    id="global_subtitle"
                                    name="global_subtitle"
                                    value={config.global_subtitle}
                                    onChange={handleInputChange}
                                    placeholder="Ex: MMD Global"
                                    className={inputClass}
                                    data-testid="settings-global-subtitle"
                                />
                            </div>
                            <div>
                                <Label htmlFor="global_title" className={labelClass}>Titre principal</Label>
                                <Input
                                    id="global_title"
                                    name="global_title"
                                    value={config.global_title}
                                    onChange={handleInputChange}
                                    placeholder="Ex: Matériel de Bureau"
                                    className={inputClass}
                                    data-testid="settings-global-title"
                                />
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="global_description" className={labelClass}>Texte de présentation</Label>
                            <Textarea
                                id="global_description"
                                name="global_description"
                                value={config.global_description}
                                onChange={handleInputChange}
                                rows={3}
                                placeholder="Décrivez votre activité de matériel de bureau..."
                                className={inputClass}
                                data-testid="settings-global-description"
                            />
                        </div>
                    </div>
                </div>

                {/* Monde MMD Luxury Cars */}
                <div className={`rounded-xl p-6 ${cardClass}`}>
                    <div className="flex items-center gap-3 mb-4">
                        {config.logo_luxury && (
                            <img src={config.logo_luxury} alt="MMD Luxury" className="w-10 h-10 rounded-full object-cover" />
                        )}
                        <h2 className={`font-heading text-lg font-medium ${titleClass}`}>Monde MMD Luxury Cars (Voitures)</h2>
                    </div>
                    <p className={`text-sm mb-4 ${theme === 'dark' ? 'text-mmd-silver/70' : 'text-mmd-navy/60'}`}>
                        Titre et texte affichés dans la section "Nos Activités" de la page d'accueil.
                    </p>
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="luxury_subtitle" className={labelClass}>Sous-titre (Marque)</Label>
                                <Input
                                    id="luxury_subtitle"
                                    name="luxury_subtitle"
                                    value={config.luxury_subtitle}
                                    onChange={handleInputChange}
                                    placeholder="Ex: MMD Luxury Cars"
                                    className={inputClass}
                                    data-testid="settings-luxury-subtitle"
                                />
                            </div>
                            <div>
                                <Label htmlFor="luxury_title" className={labelClass}>Titre principal</Label>
                                <Input
                                    id="luxury_title"
                                    name="luxury_title"
                                    value={config.luxury_title}
                                    onChange={handleInputChange}
                                    placeholder="Ex: Voitures de Luxe"
                                    className={inputClass}
                                    data-testid="settings-luxury-title"
                                />
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="luxury_description" className={labelClass}>Texte de présentation</Label>
                            <Textarea
                                id="luxury_description"
                                name="luxury_description"
                                value={config.luxury_description}
                                onChange={handleInputChange}
                                rows={3}
                                placeholder="Décrivez votre activité de voitures de luxe..."
                                className={inputClass}
                                data-testid="settings-luxury-description"
                            />
                        </div>
                    </div>
                </div>

                {/* Hero Section */}
                <div className={`rounded-xl p-6 ${cardClass}`}>
                    <h2 className={`font-heading text-lg font-medium mb-4 ${titleClass}`}>Section Hero (Accueil)</h2>
                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="hero_title" className={labelClass}>Titre principal</Label>
                            <Input
                                id="hero_title"
                                name="hero_title"
                                value={config.hero_title}
                                onChange={handleInputChange}
                                className={inputClass}
                                data-testid="settings-hero-title"
                            />
                        </div>
                        <div>
                            <Label htmlFor="hero_subtitle" className={labelClass}>Sous-titre</Label>
                            <Textarea
                                id="hero_subtitle"
                                name="hero_subtitle"
                                value={config.hero_subtitle}
                                onChange={handleInputChange}
                                rows={2}
                                className={inputClass}
                                data-testid="settings-hero-subtitle"
                            />
                        </div>
                    </div>
                </div>

                {/* Mission & Vision */}
                <div className={`rounded-xl p-6 ${cardClass}`}>
                    <h2 className={`font-heading text-lg font-medium mb-4 ${titleClass}`}>Mission & Vision</h2>
                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="mission_text" className={labelClass}>Texte Mission</Label>
                            <Textarea
                                id="mission_text"
                                name="mission_text"
                                value={config.mission_text}
                                onChange={handleInputChange}
                                rows={3}
                                className={inputClass}
                                data-testid="settings-mission"
                            />
                        </div>
                        <div>
                            <Label htmlFor="vision_text" className={labelClass}>Texte Vision</Label>
                            <Textarea
                                id="vision_text"
                                name="vision_text"
                                value={config.vision_text}
                                onChange={handleInputChange}
                                rows={3}
                                className={inputClass}
                                data-testid="settings-vision"
                            />
                        </div>
                    </div>
                </div>

                {/* Coordonnées */}
                <div className={`rounded-xl p-6 ${cardClass}`}>
                    <h2 className={`font-heading text-lg font-medium mb-4 ${titleClass}`}>Coordonnées</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="coordonnees_telephone" className={labelClass}>Téléphone</Label>
                            <Input
                                id="coordonnees_telephone"
                                name="coordonnees_telephone"
                                value={config.coordonnees_telephone}
                                onChange={handleInputChange}
                                className={inputClass}
                                data-testid="settings-telephone"
                            />
                        </div>
                        <div>
                            <Label htmlFor="coordonnees_email" className={labelClass}>Email</Label>
                            <Input
                                id="coordonnees_email"
                                name="coordonnees_email"
                                type="email"
                                value={config.coordonnees_email}
                                onChange={handleInputChange}
                                className={inputClass}
                                data-testid="settings-email"
                            />
                        </div>
                        <div className="sm:col-span-2">
                            <Label htmlFor="coordonnees_adresse" className={labelClass}>Adresse</Label>
                            <Input
                                id="coordonnees_adresse"
                                name="coordonnees_adresse"
                                value={config.coordonnees_adresse}
                                onChange={handleInputChange}
                                className={inputClass}
                                data-testid="settings-adresse"
                            />
                        </div>
                    </div>
                </div>

                <Button
                    type="submit"
                    disabled={saving}
                    data-testid="settings-save"
                    className={`px-8 py-5 ${theme === 'dark' ? 'bg-mmd-gold text-mmd-dark-bg hover:bg-mmd-gold-hover' : 'bg-mmd-navy text-white hover:bg-mmd-navy-hover'}`}
                >
                    <Gear size={18} className="mr-2" />
                    {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
                </Button>
            </form>
        </div>
    );
}
